import React, { ComponentProps } from 'react';
import styled from 'styled-components';
import { formatDistanceToNow } from '../../utils/format/formatDistanceToNow';
import { Skeleton } from '../feedback/Skeleton';
import { Box } from '../layout/Box';
import { Flex } from '../layout/Flex';
import { MediaCover } from '../media/MediaCover';
import { Typography } from '../typography/Typography';
import { MediaPreview } from './MediaPreview';
import { PostSource } from './constants';
import type { PostUI } from './types';

const DEFAULT_VERTICAL_GAP = 1.5;
const DEFAULT_HORIZONTAL_GAP = { xs: 2, sm: 3 };

const Root = styled(Flex).attrs((props) => ({
    ...props,
    width: '100%',
    direction: 'row',
    py: DEFAULT_VERTICAL_GAP,
    px: DEFAULT_HORIZONTAL_GAP,
    spacing: DEFAULT_HORIZONTAL_GAP,
    alignItems: 'stretch',
    justifyContent: 'space-between',
}))`
    cursor: pointer;
    background-color: var(--background-primary);
    transition: background-color 200ms cubic-bezier(0.25, 0.1, 0.25, 1);

    &:hover {
        background-color: var(--background-secondary);
    }

    &:not(:last-child) {
        border-bottom: 1px solid var(--divider);
    }
`;

const getSource = (post: PostUI) => {
    if (post.source === PostSource.INSTAGRAM) {
        return 'Instagram*';
    }

    if (post.source === PostSource.TELEGRAM) {
        return 'Telegram';
    }

    return 'Яндекс Ритм';
};

interface DraftSelectionRowProps extends ComponentProps<typeof Flex> {
    post: PostUI;
}

export const DraftSelectionRow = (props: DraftSelectionRowProps) => {
    const { post, ...other } = props;

    return (
        <Root {...other}>
            <MediaCover minWidth={64} height={64} ratio={1} borderRadius={1} overflow="hidden">
                <MediaPreview media={post.content[0]} namespace="grid" />
            </MediaCover>
            <Flex direction="column" flexGrow={1} justifyContent="space-between">
                <Typography variant="caption" lines={2}>
                    {post.text}
                </Typography>
                <Typography variant="caption" color="secondary">
                    {getSource(post)}
                </Typography>
            </Flex>
            <Typography minWidth={120} textAlign="right" variant="caption">
                {formatDistanceToNow(post.createdAt)}
            </Typography>
        </Root>
    );
};

interface DraftSelectionRowSkeletonProps extends ComponentProps<typeof Flex> {}

export const DraftSelectionRowSkeleton = (props: DraftSelectionRowSkeletonProps) => (
    <Root {...props}>
        <Skeleton variant="rect" width={64} height={64} borderRadius={1} />
        <Flex direction="column" flexGrow={1} justifyContent="space-between">
            <Box width="100%">
                <Skeleton variant="text" width="100%" height={14} />
                <Skeleton variant="text" width="80%" height={14} />
            </Box>
            <Skeleton variant="text" width={100} height={14} />
        </Flex>
        <Skeleton variant="text" minWidth={120} height={14} />
    </Root>
);
